import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Beaker, Building, Info, ChevronUp, Server, X, Terminal, ArrowUpCircle, Wifi, WifiOff, AlertCircle, Settings, AlertOctagon, User as UserIcon, Copy, Check, Clock, ShieldCheck, KeyRound, RefreshCw, ToggleLeft, ToggleRight, Eye, EyeOff, Flag, AlertTriangle, Terminal as TerminalIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { getVersionInfo } from '../../services/versionService';
import { getVersion } from '../../version';
import api from '../../services/api';
import { FeatureFlagsContext } from './FeatureFlags';
import { subscribeApiError, getLastApiError, clearLastApiError } from '../../services/devTools';
import { detectScopeFromUrl } from '../../services/shadowSession';

// QA "view as" personas. `role` is the canonical BACKEND role name (the shadow
// endpoint validates against these); is_internal/is_superuser drive the client
// role-gating overlay so menus/routes match what the backend now enforces.
const QA_PERSONAS = [
  { id: 'program_admin', label: 'Program Admin', role: 'Program Admin', is_internal: true, is_superuser: false },
  { id: 'data_manager', label: 'Data Manager', role: 'Data Manager', is_internal: true, is_superuser: false },
  { id: 'study_team', label: 'Study Team', role: 'Study Team', is_internal: false, is_superuser: false },
  { id: 'study_coordinator', label: 'Study Coordinator', role: 'Study Coordinator', is_internal: false, is_superuser: false },
  { id: 'investigator', label: 'Investigator', role: 'Investigator', is_internal: false, is_superuser: false },
  { id: 'cra', label: 'CRA', role: 'CRA', is_internal: false, is_superuser: false },
];

// Global singleton to prevent duplicate maintenance polling
let maintenanceIntervalId = null;
let maintenancePollingActive = false;

/**
 * Enhanced banner component for displaying environment and sponsor information
 * Visible to internal users or when running in local development with collapsible functionality
 * 
 * @param {boolean} participantMode - If true, shows banner for participants in dev/UAT only
 */
const EnvironmentBanner = ({ participantMode = false }) => {
  const { user, realUser, qaPersona, setQaPersona, isAuthenticated, userType, userPermissions, refreshToken } = useAuth();
  // Identity in the banner always reflects the true logged-in user, even while a
  // QA persona overlay is active (so the banner can't hide itself / trap you).
  const realIdentity = realUser || user;
  const featureFlagsCtx = useContext(FeatureFlagsContext) || {};
  const { featureFlags = {}, toggleFeature, resetFeatureFlags, registry = [] } = featureFlagsCtx;
  const [lastApiError, setLastApiError] = useState(() => getLastApiError());
  const [refreshing, setRefreshing] = useState(false);
  // Persona ("view as") dropdown state
  const [personaMenuOpen, setPersonaMenuOpen] = useState(false);
  const [personaBusy, setPersonaBusy] = useState(false);
  const [personaError, setPersonaError] = useState(null);

  // Keep the "last API error" panel live.
  useEffect(() => {
    const unsub = subscribeApiError(setLastApiError);
    return unsub;
  }, []);

  // Close the persona menu on any outside click.
  useEffect(() => {
    if (!personaMenuOpen) return;
    const onDocClick = (e) => {
      if (!e.target.closest?.('[data-persona-menu]')) setPersonaMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [personaMenuOpen]);
  const [versionInfo, setVersionInfo] = useState({
    version: null,
    environment: null,
    build_date: null
  });
  const [clientVersion, setClientVersion] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMinimized, setIsMinimized] = useState(() => {
    // Check if the banner was previously minimized
    return localStorage.getItem('envBannerMinimized') === 'true';
  });
  const [isScreenBuilder, setIsScreenBuilder] = useState(false);
  // Network status state
  const [networkStatus, setNetworkStatus] = useState({
    isOnline: navigator.onLine,
    isSlowConnection: false,
    quality: 'unknown',
  });
  
  // Maintenance mode state
  const [maintenanceMode, setMaintenanceMode] = useState({
    enabled: false,
    checking: true,
    lastChecked: null
  });

  // Copy-to-clipboard feedback ("which field was just copied")
  const [copied, setCopied] = useState(null);

  // API health probe (round-trip latency + reachability), refreshed while expanded
  const [apiHealth, setApiHealth] = useState({ status: 'unknown', latencyMs: null, checking: false });
  // Viewport size, kept current on resize for layout debugging
  const [viewport, setViewport] = useState({ w: window.innerWidth, h: window.innerHeight });
  useEffect(() => {
    const onResize = () => setViewport({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Ticking clock so the token-expiry countdown updates live while expanded
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!isExpanded) return;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [isExpanded]);

  // Probe API round-trip latency + reachability while the panel is expanded.
  // FECOA-2012: this used to ping /api/dev/maintenance-status/, which is now
  // internal-only. /api/health/ is the public liveness endpoint and reports
  // nothing about the system, so the probe works for every signed-in role.
  const pingApi = useCallback(async () => {
    setApiHealth((h) => ({ ...h, checking: true }));
    const start = performance.now();
    try {
      await api.get('/api/health/');
      setApiHealth({ status: 'ok', latencyMs: Math.round(performance.now() - start), checking: false });
    } catch (e) {
      setApiHealth({
        status: e?.response ? `HTTP ${e.response.status}` : 'unreachable',
        latencyMs: Math.round(performance.now() - start),
        checking: false,
      });
    }
  }, []);

  useEffect(() => {
    if (!isExpanded) return;
    pingApi();
    const id = setInterval(pingApi, 15000);
    return () => clearInterval(id);
  }, [isExpanded, pingApi]);

  // Check for screenbuilder-active class
  useEffect(() => {
    const checkScreenBuilderClass = () => {
      setIsScreenBuilder(document.body.classList.contains('screenbuilder-active'));
    };
    
    checkScreenBuilderClass();
    
    const observer = new MutationObserver(checkScreenBuilderClass);
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    // Show banner for internal users, Program Admins, OR when running locally
    const isLocalDevelopment = import.meta.env.DEV || 
                              window.location.hostname === 'localhost' || 
                              window.location.hostname === '127.0.0.1' ||
                              window.location.port === '5173';

    const isProgramAdmin = realIdentity?.role === 'Program Admin';

    // In participant mode, show banner only in dev/UAT environments
    // We need to fetch version info first to check the environment
    if (participantMode) {
      // For participants, always fetch version info first to determine environment
      fetchVersionInfo().then(() => {
        // Will be handled by the versionInfo state update
      });
      return;
    }
    
    const shouldShowBanner = realIdentity?.is_internal || isProgramAdmin || isLocalDevelopment;

    if (shouldShowBanner) {
      setIsVisible(true);
      fetchVersionInfo();
    } else {
      setIsVisible(false);
    }
  }, [realIdentity, participantMode]);
  
  // Handle participant mode visibility based on environment
  useEffect(() => {
    if (participantMode && versionInfo.environment) {
      const envLower = versionInfo.environment.toLowerCase();
      const isDevOrUat = envLower.includes('dev') || envLower.includes('uat') || envLower.includes('development');
      setIsVisible(isDevOrUat);
    }
  }, [participantMode, versionInfo.environment]);

  // For regular (non-participant) users, restrict to dev/uat — hide in production
  useEffect(() => {
    if (!participantMode && versionInfo.environment) {
      const isLocalDevelopment = import.meta.env.DEV ||
                                window.location.hostname === 'localhost' ||
                                window.location.hostname === '127.0.0.1' ||
                                window.location.port === '5173';
      if (isLocalDevelopment) return; // Always show in local dev regardless of env var
      const envLower = versionInfo.environment.toLowerCase();
      const isDevOrUat = envLower.includes('dev') || envLower.includes('uat') || envLower.includes('development');
      if (!isDevOrUat) {
        setIsVisible(false);
      }
    }
  }, [participantMode, versionInfo.environment]);

  // Separate useEffect for maintenance polling to prevent re-mounting issues
  useEffect(() => {
    if (!isVisible) return;
    
    // Exit early if polling is already active to prevent duplicate intervals
    if (maintenancePollingActive) {
      console.log('[EnvironmentBanner] Maintenance polling already active, skipping setup');
      return;
    }

    console.log('[EnvironmentBanner] Starting maintenance polling setup');

    // Initialize network status checking
    checkConnectionStatus();
    
    // Initialize maintenance mode check
    checkMaintenanceStatus();
    
    // Set up network event listeners
    window.addEventListener('online', handleOnlineStatusChange);
    window.addEventListener('offline', handleOnlineStatusChange);
    
    // Check connection quality every minute
    const networkIntervalId = setInterval(checkConnectionStatus, 60000);
    
    // Reduce maintenance mode polling frequency based on environment
    // In Azure environments, check very infrequently to prevent 429 rate limit errors
    // NOTE: when we move to a proper production environment and hostname we need to change the criteria for the azure env check
    const isAzureEnvironment = window.location.hostname.includes('azure') || window.location.hostname.includes('azurewebsites.net');
    const isLocalDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    
    // Much less frequent checks: 30 min for Azure, 20 min for prod, 15 min for local dev
    let maintenanceCheckInterval;
    if (isAzureEnvironment) {
      maintenanceCheckInterval = 1800000; // 30 minutes for Azure
    } else if (isLocalDev) {
      maintenanceCheckInterval = 900000; // 15 minutes for local dev
    } else {
      maintenanceCheckInterval = 1200000; // 20 minutes for other environments
    }
    
    // Use global singleton to prevent duplicate maintenance polling
    if (!maintenancePollingActive) {
      console.log(`[EnvironmentBanner] Setting maintenance check interval to ${maintenanceCheckInterval/60000} minutes for ${isAzureEnvironment ? 'Azure' : isLocalDev ? 'local dev' : 'production'} environment`);
      maintenanceIntervalId = setInterval(checkMaintenanceStatus, maintenanceCheckInterval);
      maintenancePollingActive = true;
    } else {
      console.log('[EnvironmentBanner] Maintenance polling already active, skipping duplicate setup');
    }
    
    return () => {
      console.log('[EnvironmentBanner] Cleaning up intervals');
      window.removeEventListener('online', handleOnlineStatusChange);
      window.removeEventListener('offline', handleOnlineStatusChange);
      clearInterval(networkIntervalId);
      
      // Clear global maintenance polling
      if (maintenanceIntervalId) {
        clearInterval(maintenanceIntervalId);
        maintenanceIntervalId = null;
        maintenancePollingActive = false;
        console.log('[EnvironmentBanner] Global maintenance polling stopped');
      }
    };
  }, []); // Empty dependency array - only run once on mount

  const fetchVersionInfo = async () => {
    try {
      const info = await getVersionInfo();
      console.debug('Version info fetched:', info);
      setVersionInfo(info);

      // Get client version from version.js
      const clientVersionStr = getVersion();
      setClientVersion(clientVersionStr);
    } catch (error) {
      console.error('Error fetching version info:', error);
    }
  };
  
  // Network status handling functions
  const handleOnlineStatusChange = () => {
    setNetworkStatus(prev => ({
      ...prev,
      isOnline: navigator.onLine
    }));
  };
  
  const checkConnectionStatus = () => {
    // Simple connection quality detection fallback
    const connectionType = navigator.connection?.effectiveType || 'unknown';
    const isSlowConnection = connectionType === 'slow-2g' || connectionType === '2g';
    
    setNetworkStatus({
      isOnline: navigator.onLine,
      isSlowConnection,
      quality: connectionType,
    });
  };
  
  // Check maintenance mode status
  const checkMaintenanceStatus = async () => {
    try {
      const response = await api.get('/api/dev/maintenance-status/');
      setMaintenanceMode({
        enabled: response.data.is_maintenance_mode,
        checking: false,
        lastChecked: new Date()
      });
    } catch (error) {
      // FECOA-2012: /api/dev/ is internal-only now, so a non-internal signed-in
      // user gets 401/403 here. That is expected, not a failure worth logging;
      // the indicator just stays off for them.
      const status = error?.response?.status;
      if (status !== 401 && status !== 403) {
        console.error('Error checking maintenance mode status:', error);
      }
      setMaintenanceMode(prev => ({
        ...prev,
        checking: false,
        lastChecked: new Date()
      }));
    }
  };

  // Save minimized state to localStorage
  const toggleMinimized = () => {
    const newState = !isMinimized;
    setIsMinimized(newState);
    localStorage.setItem('envBannerMinimized', newState.toString());
  };

  // Hide banner when not visible
  if (!isVisible) return null;

  // Determine environment color and icon
  const getEnvironmentData = (env) => {
    const envLower = env?.toLowerCase() || '';
    
    if (envLower.includes('prod')) {
      return {
        color: 'bg-rose-500',
        textColor: 'text-white',
        icon: <Terminal className="w-3.5 h-3.5" />,
        ringColor: 'ring-rose-600'
      };
    } else if (envLower.includes('stag') || envLower.includes('uat')) {
      return {
        color: 'bg-amber-500',
        textColor: 'text-white',
        icon: <Server className="w-3.5 h-3.5" />,
        ringColor: 'ring-amber-600'
      };
    } else if (envLower.includes('dev')) {
      return {
        color: 'bg-emerald-500',
        textColor: 'text-white',
        icon: <Beaker className="w-3.5 h-3.5" />,
        ringColor: 'ring-emerald-600'
      };
    } else {
      return {
        color: 'bg-gray-500',
        textColor: 'text-white',
        icon: <Info className="w-3.5 h-3.5" />,
        ringColor: 'ring-gray-600'
      };
    }
  };

  // Get sponsor info from user or localStorage
  const getSponsorInfo = () => {
    // Check for sponsor in user object first
    if (user?.sponsor) {
      return {
        name: user.sponsor.name,
        id: user.sponsor.id
      };
    }

    // Fallback to localStorage
    try {
      const storedSponsor = localStorage.getItem('selectedSponsor');
      if (storedSponsor) {
        const sponsorData = JSON.parse(storedSponsor);
        return {
          name: sponsorData.name,
          id: sponsorData.id
        };
      }
    } catch (e) {
      console.error('Error parsing sponsor data:', e);
    }
    
    return {
      name: null,
      id: null
    };
  };

  // Decode a JWT payload without verifying (debug display only)
  const decodeJwt = (token) => {
    try {
      const payload = token.split('.')[1];
      const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
      return JSON.parse(json);
    } catch {
      return null;
    }
  };

  // Format a millisecond duration as "1h 23m 04s" (or "expired")
  const formatDuration = (ms) => {
    if (ms <= 0) return 'expired';
    const totalSec = Math.floor(ms / 1000);
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    const pad = (n) => String(n).padStart(2, '0');
    if (h > 0) return `${h}h ${pad(m)}m ${pad(s)}s`;
    if (m > 0) return `${m}m ${pad(s)}s`;
    return `${s}s`;
  };

  // Build a copy-to-clipboard handler with brief visual feedback
  const copyToClipboard = (value, key) => {
    try {
      navigator.clipboard.writeText(String(value));
      setCopied(key);
      setTimeout(() => setCopied((c) => (c === key ? null : c)), 1200);
    } catch (e) {
      console.error('Clipboard write failed:', e);
    }
  };

  const sponsorInfo = getSponsorInfo();
  const envData = getEnvironmentData(versionInfo.environment);

  // Auth token diagnostics (caregiver session token in sessionStorage wins)
  const authToken = sessionStorage.getItem('authToken') || localStorage.getItem('authToken');
  const tokenPayload = authToken ? decodeJwt(authToken) : null;
  const tokenExpMs = tokenPayload?.exp ? tokenPayload.exp * 1000 : null;
  const tokenRemainingMs = tokenExpMs ? tokenExpMs - now : null;
  const tokenIssuedMs = tokenPayload?.iat ? tokenPayload.iat * 1000 : null;

  // Refresh token diagnostics
  const refreshTokenStr = localStorage.getItem('refreshToken') || sessionStorage.getItem('refreshToken');
  const refreshPayload = refreshTokenStr ? decodeJwt(refreshTokenStr) : null;
  const refreshRemainingMs = refreshPayload?.exp ? refreshPayload.exp * 1000 - now : null;

  // Manual token refresh (tests the refresh path on demand)
  const handleManualRefresh = async () => {
    setRefreshing(true);
    try {
      await refreshToken?.();
    } finally {
      setRefreshing(false);
      setNow(Date.now());
    }
  };

  // Build a copy-pasteable cURL for an authenticated GET against the API base.
  const buildCurl = () => {
    const base = import.meta.env.VITE_API_URL || window.location.origin;
    return `curl '${base}/api/user/info/' \\\n  -H 'Authorization: Bearer ${authToken || '<token>'}'`;
  };

  const gitSha = import.meta.env.VITE_GIT_SHA || 'unknown';
  const apiBase = import.meta.env.VITE_API_URL || '(proxy) ' + window.location.origin;
  const conn = navigator.connection || {};

  // Detected study/site scope from the current URL (auto-applied to a persona).
  const detectedScope = detectScopeFromUrl();

  // Enter a persona (scoped to the current URL) or exit (persona=null). Mints +
  // swaps the shadow token via AuthContext; surfaces backend errors inline.
  const handleSelectPersona = async (persona) => {
    setPersonaError(null);
    setPersonaBusy(true);
    try {
      if (persona) {
        await setQaPersona({ ...persona, scope: detectScopeFromUrl() });
      } else {
        await setQaPersona(null);
      }
      setPersonaMenuOpen(false);
    } catch (e) {
      setPersonaError(e?.response?.data?.detail || e?.message || 'Failed to switch persona');
    } finally {
      setPersonaBusy(false);
    }
  };
  const tokenExpiryColor =
    tokenRemainingMs == null
      ? 'text-gray-400'
      : tokenRemainingMs <= 0
      ? 'text-red-400'
      : tokenRemainingMs < 120000
      ? 'text-red-400'
      : tokenRemainingMs < 300000
      ? 'text-yellow-400'
      : 'text-green-400';
  const permissionCount =
    userPermissions && typeof userPermissions === 'object'
      ? Object.values(userPermissions).filter(Boolean).length
      : 0;

  // If minimized, just show a pull tab
  if (isMinimized) {
    return (
      <div className="env-dev-banner fixed bottom-0 right-8 z-[9999]">  
        <motion.button
          onClick={toggleMinimized}
          className="flex items-center space-x-2 rounded-t-lg bg-gray-800 px-4 py-1.5 text-white shadow-float transition-colors hover:bg-gray-700"
          initial={{ y: 20 }}
          animate={{ y: 0 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
        >
          <ArrowUpCircle size={14} />
          <span className="text-xs font-medium">
            {versionInfo.environment?.toUpperCase() || 'ENV'} INFO v{clientVersion || '?'}
          </span>
        </motion.button>
      </div>
    );
  }

  return (
    <AnimatePresence>
      <motion.div 
        className="env-dev-banner fixed bottom-0 left-0 right-0 z-[9999] border-t border-gray-700 bg-gray-800 py-1.5 text-white shadow-float"
        initial={{ y: 100 }}
        animate={{ y: 0 }}
        exit={{ y: 100 }}
        transition={{ duration: 0.25, ease: 'easeOut' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {/* Environment Badge */}
              <div className="flex items-center">
                <div className={`${envData.color} ${envData.textColor} px-2.5 py-1 rounded-md flex items-center space-x-1.5 text-xs font-medium ring-1 ${envData.ringColor}`}>
                  {envData.icon}
                  <span>{(versionInfo.environment || 'UNKNOWN ENV').toUpperCase()}</span>
                </div>
                
                {/* Maintenance Mode Indicator */}
                {maintenanceMode.enabled && (
                  <div className="ml-2 flex items-center space-x-1 rounded-md bg-warning-100 px-3 py-1 text-xs font-medium text-warning-800">
                    <Settings className="w-3.5 h-3.5" />
                    <span>MAINTENANCE MODE</span>
                  </div>
                )}
              </div>
              
              {/* Sponsor Badge */}
              {sponsorInfo.name && (
                <div className="flex items-center text-xs">
                  <div className="flex items-center space-x-1.5 bg-white/10 px-2.5 py-1 rounded-md text-white font-medium border border-white/20">
                    <Building className="w-3.5 h-3.5" />
                    <span>{sponsorInfo.name}</span>
                  </div>
                </div>
              )}
              
              {/* Version Badge */}
              {versionInfo.version && (
                <div className="flex items-center">
                  <span className="text-xs text-gray-300 font-mono">
                    v{versionInfo.version}
                  </span>
                </div>
              )}

              {/* User badge → click to hot-swap persona (dropup when collapsed,
                  dropdown when expanded). Shows real identity, or the active
                  persona when impersonating. */}
              {realIdentity?.email && (
                <div className="relative flex items-center text-xs" data-persona-menu>
                  <button
                    onClick={() => setPersonaMenuOpen((o) => !o)}
                    className={`flex items-center space-x-1.5 px-2.5 py-1 rounded-md font-medium border transition-colors ${
                      qaPersona
                        ? 'bg-primary-500/30 text-primary-100 border-primary-400/40 hover:bg-primary-500/40'
                        : 'bg-white/10 text-white border-white/20 hover:bg-white/20'
                    }`}
                    title="Switch persona (view as role)"
                  >
                    {qaPersona ? <Eye className="w-3.5 h-3.5" /> : <UserIcon className="w-3.5 h-3.5" />}
                    <span>{realIdentity.email}</span>
                    <span className={`font-normal border-l pl-1.5 ${qaPersona ? 'text-primary-200/90 border-primary-400/40' : 'text-gray-300 border-white/20'}`}>
                      {qaPersona ? `as ${qaPersona.label}` : realIdentity.role}
                    </span>
                    {qaPersona && <span className="text-primary-200/70">· read-only</span>}
                    <ChevronUp className={`w-3 h-3 ml-0.5 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                  </button>

                  {personaMenuOpen && (
                    <div
                      className={`absolute right-0 z-[10000] w-64 rounded-lg bg-gray-900 border border-gray-700 shadow-overlay p-1 ${
                        isExpanded ? 'top-full mt-2' : 'bottom-full mb-2'
                      }`}
                    >
                      <div className="px-2 py-1 text-[11px] text-gray-500 flex items-center justify-between">
                        <span>View as role · read-only</span>
                        {personaBusy && <RefreshCw size={11} className="animate-spin" />}
                      </div>
                      <div className="px-2 pb-1 text-[11px] text-gray-400">
                        Scope:{' '}
                        {detectedScope.studyId || detectedScope.siteId ? (
                          <span className="text-emerald-400">
                            {detectedScope.studyId ? `study ${detectedScope.studyId}` : ''}
                            {detectedScope.siteId ? ` · site ${detectedScope.siteId}` : ''}
                          </span>
                        ) : (
                          <span className="text-gray-500">whole sponsor (no study/site in URL)</span>
                        )}
                      </div>
                      {QA_PERSONAS.map((p) => {
                        const active = qaPersona?.id === p.id;
                        return (
                          <button
                            key={p.id}
                            disabled={personaBusy}
                            onClick={() => handleSelectPersona(active ? null : p)}
                            className={`w-full flex items-center justify-between px-2 py-1.5 rounded text-left transition-colors disabled:opacity-50 ${
                              active ? 'bg-primary-500/30 text-white' : 'text-gray-200 hover:bg-white/10'
                            }`}
                          >
                            <span>{p.label}</span>
                            {active && <Check size={12} className="text-primary-300" />}
                          </button>
                        );
                      })}
                      <div className="border-t border-gray-700 my-1" />
                      <button
                        disabled={personaBusy || !qaPersona}
                        onClick={() => handleSelectPersona(null)}
                        className="w-full flex items-center space-x-1.5 px-2 py-1.5 rounded text-left text-gray-200 hover:bg-white/10 disabled:opacity-40"
                      >
                        <EyeOff size={12} />
                        <span>Exit to {realIdentity.role || 'real user'}</span>
                      </button>
                      {personaError && (
                        <p className="px-2 py-1 text-[11px] text-red-400 flex items-start">
                          <AlertTriangle size={11} className="mr-1 mt-0.5 shrink-0" />
                          {personaError}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
              
              {/* Network Status Indicator */}
              <div className="text-xs flex items-center space-x-1.5 ml-3">
                <div className={`w-1.5 h-1.5 rounded-full ${
                  !networkStatus.isOnline 
                    ? "bg-red-500" 
                    : networkStatus.isSlowConnection
                    ? "bg-yellow-500"
                    : "bg-green-500"
                }`}></div>
                <span className={`${
                  !networkStatus.isOnline 
                    ? "text-red-300" 
                    : networkStatus.isSlowConnection
                    ? "text-yellow-300"
                    : "text-green-300"
                }`}>
                  {!networkStatus.isOnline 
                    ? "Offline" 
                    : networkStatus.isSlowConnection
                    ? "Slow Network"
                    : "Online"}
                </span>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              {/* Expand/Collapse Button */}
              <button 
                onClick={() => setIsExpanded(!isExpanded)}
                className="text-gray-400 dark:text-gray-500 hover:text-white transition-colors p-1 rounded-md hover:bg-white/10"
              >
                <ChevronUp 
                  className={`w-4 h-4 transition-transform duration-300 ${isExpanded ? 'transform rotate-180' : ''}`} 
                />
              </button>
              
              {/* Minimize Button */}
              <button 
                onClick={toggleMinimized}
                className="text-gray-400 dark:text-gray-500 hover:text-white transition-colors p-1 rounded-md hover:bg-white/10"
                title="Minimize"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
          
          {/* Expanded Details */}
          <AnimatePresence>
            {isExpanded && (
              <motion.div 
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="overflow-hidden"
              >
                <CollapsibleSection id="diag" title="Diagnostics" icon={Info} defaultOpen={true}>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-3">
                  {/* ---- Session & Auth ---- */}
                  <div>
                    <h4 className="text-gray-400 dark:text-gray-500 mb-1 flex items-center space-x-1.5">
                      <ShieldCheck className="w-3.5 h-3.5" />
                      <span>Session &amp; Auth</span>
                    </h4>
                    <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 items-center">
                      <div className="text-gray-400 dark:text-gray-500">User:</div>
                      <CopyableValue value={realIdentity?.email} fieldKey="email" copied={copied} onCopy={copyToClipboard} />

                      <div className="text-gray-400 dark:text-gray-500">Role:</div>
                      <div className="text-white font-medium">
                        {realIdentity?.role || 'N/A'}
                        {qaPersona && (
                          <span className="ml-1.5 text-primary-300">→ {qaPersona.label || qaPersona.role}</span>
                        )}
                      </div>

                      <div className="text-gray-400 dark:text-gray-500">User Type:</div>
                      <div className="text-white font-medium">{userType || 'N/A'}</div>

                      <div className="text-gray-400 dark:text-gray-500">User ID:</div>
                      <CopyableValue value={realIdentity?.id} fieldKey="userId" copied={copied} onCopy={copyToClipboard} mono />

                      <div className="text-gray-400 dark:text-gray-500">Internal:</div>
                      <div className="text-white font-medium">{realIdentity?.is_internal ? 'Yes' : 'No'}{realIdentity?.is_superuser ? ' · superuser' : ''}</div>

                      <div className="text-gray-400 dark:text-gray-500">Permissions:</div>
                      <div className="text-white font-medium">{permissionCount} granted</div>
                    </div>
                  </div>

                  {/* ---- Token & Build ---- */}
                  <div>
                    <h4 className="text-gray-400 dark:text-gray-500 mb-1 flex items-center space-x-1.5">
                      <KeyRound className="w-3.5 h-3.5" />
                      <span>Token &amp; Build</span>
                    </h4>
                    <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 items-center">
                      <div className="text-gray-400 dark:text-gray-500">Access expires:</div>
                      <div className={`font-medium font-mono flex items-center ${tokenExpiryColor}`}>
                        <Clock size={12} className="mr-1" />
                        {tokenRemainingMs == null ? 'N/A' : formatDuration(tokenRemainingMs)}
                        <button
                          onClick={handleManualRefresh}
                          disabled={refreshing}
                          className="ml-2 text-gray-400 hover:text-white disabled:opacity-50"
                          title="Refresh token now"
                        >
                          <RefreshCw size={12} className={refreshing ? 'animate-spin' : ''} />
                        </button>
                      </div>

                      <div className="text-gray-400 dark:text-gray-500">Refresh expires:</div>
                      <div className="text-white font-medium font-mono">
                        {refreshRemainingMs == null ? 'N/A' : formatDuration(refreshRemainingMs)}
                      </div>

                      <div className="text-gray-400 dark:text-gray-500">Issued:</div>
                      <div className="text-white font-medium">
                        {tokenIssuedMs ? new Date(tokenIssuedMs).toLocaleTimeString() : 'N/A'}
                      </div>

                      <div className="text-gray-400 dark:text-gray-500">Token:</div>
                      <CopyableValue
                        value={authToken}
                        display={authToken ? `${authToken.slice(0, 10)}…` : 'N/A'}
                        fieldKey="token"
                        copied={copied}
                        onCopy={copyToClipboard}
                        mono
                      />

                      <div className="text-gray-400 dark:text-gray-500">API Version:</div>
                      <div className="text-white font-medium">{versionInfo.version || 'N/A'}</div>

                      <div className="text-gray-400 dark:text-gray-500">Client Version:</div>
                      <div className="text-white font-medium">{clientVersion || 'N/A'}</div>

                      <div className="text-gray-400 dark:text-gray-500">Build Date:</div>
                      <div className="text-white font-medium">{versionInfo.build_date || 'N/A'}</div>

                      <div className="text-gray-400 dark:text-gray-500">Git SHA:</div>
                      <CopyableValue value={gitSha} fieldKey="gitSha" copied={copied} onCopy={copyToClipboard} mono />
                    </div>
                  </div>

                  {/* ---- System & Network ---- */}
                  <div>
                    <h4 className="text-gray-400 dark:text-gray-500 mb-1 flex items-center space-x-1.5">
                      <Server className="w-3.5 h-3.5" />
                      <span>System &amp; Network</span>
                    </h4>
                    <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 items-center">
                      <div className="text-gray-400 dark:text-gray-500">Environment:</div>
                      <div className="text-white font-medium">{versionInfo.environment || 'N/A'}</div>

                      <div className="text-gray-400 dark:text-gray-500">API base:</div>
                      <CopyableValue value={apiBase} fieldKey="apiBase" copied={copied} onCopy={copyToClipboard} mono />

                      <div className="text-gray-400 dark:text-gray-500">API health:</div>
                      <div className="font-medium flex items-center">
                        <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
                          apiHealth.status === 'ok' ? 'bg-green-500' : apiHealth.status === 'unknown' ? 'bg-gray-500' : 'bg-red-500'
                        }`}></span>
                        <span className={apiHealth.status === 'ok' ? 'text-green-400' : apiHealth.status === 'unknown' ? 'text-gray-400' : 'text-red-400'}>
                          {apiHealth.checking ? 'pinging…' : apiHealth.status}
                          {apiHealth.latencyMs != null && !apiHealth.checking ? ` · ${apiHealth.latencyMs}ms` : ''}
                        </span>
                        <button onClick={pingApi} className="ml-2 text-gray-400 hover:text-white" title="Re-ping">
                          <RefreshCw size={11} className={apiHealth.checking ? 'animate-spin' : ''} />
                        </button>
                      </div>

                      <div className="text-gray-400 dark:text-gray-500">Origin:</div>
                      <div className="text-white font-medium">{window.location.host}</div>

                      <div className="text-gray-400 dark:text-gray-500">Sponsor:</div>
                      <div className="text-white font-medium">{sponsorInfo.name || 'N/A'}</div>

                      <div className="text-gray-400 dark:text-gray-500">Sponsor ID:</div>
                      <CopyableValue value={sponsorInfo.id} fieldKey="sponsorId" copied={copied} onCopy={copyToClipboard} mono />

                      <div className="text-gray-400 dark:text-gray-500">Maintenance:</div>
                      <div className="text-white font-medium">
                        {maintenanceMode.checking ? 'Checking...' : maintenanceMode.enabled ? (
                          <span className="text-yellow-400 inline-flex items-center space-x-1.5">
                            <Settings className="w-3.5 h-3.5" />
                            <span>Enabled</span>
                          </span>
                        ) : 'Disabled'}
                        {maintenanceMode.lastChecked && (
                          <span className="text-gray-500 ml-1.5">
                            ({maintenanceMode.lastChecked.toLocaleTimeString()})
                          </span>
                        )}
                      </div>

                      <div className="text-gray-400 dark:text-gray-500">Network:</div>
                      <div className="text-white font-medium flex items-center">
                        {!networkStatus.isOnline ? (
                          <>
                            <WifiOff size={12} className="mr-1 text-red-400" />
                            <span className="text-red-400">Offline</span>
                          </>
                        ) : networkStatus.isSlowConnection ? (
                          <>
                            <AlertCircle size={12} className="mr-1 text-yellow-400" />
                            <span className="text-yellow-400">Slow</span>
                          </>
                        ) : (
                          <>
                            <Wifi size={12} className="mr-1 text-green-400" />
                            <span className="text-green-400">Online</span>
                          </>
                        )}
                        {conn.effectiveType && (
                          <span className="text-gray-400 ml-1.5">
                            {conn.effectiveType}{conn.downlink ? ` · ${conn.downlink}Mb/s` : ''}{conn.rtt != null ? ` · ${conn.rtt}ms rtt` : ''}
                          </span>
                        )}
                      </div>

                      <div className="text-gray-400 dark:text-gray-500">Last API error:</div>
                      <div className="font-medium min-w-0">
                        {lastApiError ? (
                          <span className="text-red-400 flex items-center min-w-0">
                            <AlertTriangle size={12} className="mr-1 shrink-0" />
                            <span className="truncate">
                              {lastApiError.status || 'ERR'} {lastApiError.method} {lastApiError.url}
                            </span>
                            <button onClick={() => clearLastApiError()} className="ml-1.5 shrink-0 text-gray-500 hover:text-white" title="Clear">
                              <X size={11} />
                            </button>
                          </span>
                        ) : (
                          <span className="text-gray-500">none</span>
                        )}
                      </div>

                      <div className="text-gray-400 dark:text-gray-500">Viewport:</div>
                      <div className="text-white font-medium">{viewport.w}×{viewport.h} @{window.devicePixelRatio}x</div>

                      <div className="text-gray-400 dark:text-gray-500">Locale / TZ:</div>
                      <div className="text-white font-medium truncate">
                        {Intl.DateTimeFormat().resolvedOptions().timeZone} · {new Date(now).toLocaleTimeString()}
                      </div>
                    </div>
                  </div>
                </div>
                </CollapsibleSection>

                {/* ---- QA: View as persona (read-only) ---- */}
                <CollapsibleSection
                  id="qa"
                  title="QA — View as persona"
                  icon={Eye}
                  defaultOpen={!!qaPersona}
                  right={qaPersona ? (
                    <button
                      onClick={() => handleSelectPersona(null)}
                      disabled={personaBusy}
                      className="flex items-center space-x-1 rounded-md border border-primary-400/40 bg-primary-500/30 px-2 py-0.5 text-primary-100 transition-colors hover:bg-primary-500/50 disabled:opacity-50"
                    >
                      <EyeOff size={11} />
                      <span>Exit ({qaPersona.label || qaPersona.role})</span>
                    </button>
                  ) : null}
                >
                  <p className="text-gray-400 mb-1.5">
                    Mints a real read-only backend token for the role, scoped to{' '}
                    {detectedScope.studyId || detectedScope.siteId ? (
                      <span className="text-emerald-400">
                        {detectedScope.studyId ? `study ${detectedScope.studyId}` : ''}
                        {detectedScope.siteId ? ` · site ${detectedScope.siteId}` : ''}
                      </span>
                    ) : (
                      'the whole sponsor (open a study/site to scope)'
                    )}
                    . Real permissions + data, writes blocked.
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {QA_PERSONAS.map((p) => {
                      const active = qaPersona?.id === p.id;
                      return (
                        <button
                          key={p.id}
                          disabled={personaBusy}
                          onClick={() => handleSelectPersona(active ? null : p)}
                          className={`px-2.5 py-1 rounded-md border transition-colors disabled:opacity-50 ${
                            active
                              ? 'bg-primary-500/40 text-white border-primary-400/60'
                              : 'bg-white/5 text-gray-300 border-white/15 hover:bg-white/15'
                          }`}
                        >
                          {p.label}
                        </button>
                      );
                    })}
                  </div>
                  {personaError && (
                    <p className="mt-1.5 text-red-400 flex items-center">
                      <AlertTriangle size={11} className="mr-1 shrink-0" />
                      {personaError}
                    </p>
                  )}
                </CollapsibleSection>

                {/* ---- Feature flags ---- */}
                {registry.length > 0 && (
                  <CollapsibleSection
                    id="flags"
                    title="Feature flags"
                    icon={Flag}
                    defaultOpen={false}
                    right={resetFeatureFlags ? (
                      <button onClick={resetFeatureFlags} className="text-gray-400 hover:text-white">
                        Reset
                      </button>
                    ) : null}
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
                      {registry.map((flag) => {
                        const on = !!featureFlags[flag.key];
                        return (
                          <button
                            key={flag.key}
                            onClick={() => toggleFeature?.(flag.key)}
                            className="flex items-center justify-between text-left group"
                            title={flag.description}
                          >
                            <span className="min-w-0">
                              <span className="text-white font-medium">{flag.label}</span>
                              <span className="block text-gray-500 truncate">{flag.description}</span>
                            </span>
                            {on ? (
                              <ToggleRight className="w-5 h-5 text-green-400 shrink-0 ml-2" />
                            ) : (
                              <ToggleLeft className="w-5 h-5 text-gray-500 shrink-0 ml-2 group-hover:text-gray-300" />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </CollapsibleSection>
                )}

                {/* ---- Quick debug actions ---- */}
                <div className="mt-3 pt-3 border-t border-gray-700/50 flex flex-wrap items-center gap-2">
                  <button
                    onClick={() => copyToClipboard(JSON.stringify({
                      environment: versionInfo.environment,
                      apiVersion: versionInfo.version,
                      clientVersion,
                      gitSha,
                      buildDate: versionInfo.build_date,
                      user: { id: realIdentity?.id, email: realIdentity?.email, role: realIdentity?.role, userType, isInternal: realIdentity?.is_internal, isSuperuser: realIdentity?.is_superuser },
                      qaPersona: qaPersona?.role || null,
                      sponsor: sponsorInfo,
                      permissionCount,
                      featureFlags,
                      token: { issued: tokenIssuedMs, expires: tokenExpMs, remainingMs: tokenRemainingMs },
                      apiBase,
                      apiHealth,
                      lastApiError,
                      origin: window.location.host,
                      viewport: `${viewport.w}x${viewport.h}@${window.devicePixelRatio}x`,
                      userAgent: navigator.userAgent,
                    }, null, 2), 'bundle')}
                    className="flex items-center space-x-1.5 px-2.5 py-1 rounded-md bg-white/10 hover:bg-white/20 text-white text-xs font-medium border border-white/20 transition-colors"
                  >
                    {copied === 'bundle' ? <Check size={12} className="text-green-400" /> : <Copy size={12} />}
                    <span>{copied === 'bundle' ? 'Copied!' : 'Copy debug bundle'}</span>
                  </button>
                  <button
                    onClick={() => copyToClipboard(buildCurl(), 'curl')}
                    className="flex items-center space-x-1.5 px-2.5 py-1 rounded-md bg-white/10 hover:bg-white/20 text-white text-xs font-medium border border-white/20 transition-colors"
                  >
                    {copied === 'curl' ? <Check size={12} className="text-green-400" /> : <TerminalIcon size={12} />}
                    <span>{copied === 'curl' ? 'Copied!' : 'Copy as cURL'}</span>
                  </button>
                  <button
                    onClick={() => window.location.reload()}
                    className="flex items-center space-x-1.5 px-2.5 py-1 rounded-md bg-white/10 hover:bg-white/20 text-white text-xs font-medium border border-white/20 transition-colors"
                  >
                    <RefreshCw size={12} />
                    <span>Hard reload</span>
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

// Collapsible section with a clickable header; remembers its open/closed state
// per-section in localStorage so the banner stays compact between sessions.
const CollapsibleSection = ({ id, title, icon: Icon, defaultOpen = false, right = null, children }) => {
  const [open, setOpen] = useState(() => {
    try {
      const v = localStorage.getItem(`envbanner_sec_${id}`);
      return v == null ? defaultOpen : v === 'true';
    } catch {
      return defaultOpen;
    }
  });
  const toggle = () => {
    setOpen((o) => {
      const next = !o;
      try {
        localStorage.setItem(`envbanner_sec_${id}`, String(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  };
  return (
    <div className="mt-2 pt-2 border-t border-gray-700/50 text-xs">
      <div className="flex items-center justify-between">
        <button onClick={toggle} className="flex items-center space-x-1.5 text-gray-400 hover:text-white transition-colors">
          <ChevronUp className={`w-3 h-3 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
          {Icon && <Icon className="w-3.5 h-3.5" />}
          <span className="font-medium">{title}</span>
        </button>
        {open && right}
      </div>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="pt-2">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Value cell with a hover copy button and "copied" feedback
const CopyableValue = ({ value, display, fieldKey, copied, onCopy, mono = false }) => {
  if (value == null || value === '') {
    return <div className="text-white font-medium">N/A</div>;
  }
  return (
    <div className="flex items-center group min-w-0">
      <span className={`text-white font-medium truncate ${mono ? 'font-mono' : ''}`}>
        {display ?? value}
      </span>
      <button
        onClick={() => onCopy(value, fieldKey)}
        className="ml-1.5 shrink-0 text-gray-500 hover:text-white opacity-0 group-hover:opacity-100 transition-opacity"
        title="Copy"
      >
        {copied === fieldKey ? <Check size={12} className="text-green-400" /> : <Copy size={12} />}
      </button>
    </div>
  );
};

export default EnvironmentBanner;